export type StreamProfile = 'low-latency' | 'balanced' | 'quality';
export type StreamQuality = 'auto' | '720p' | '1080p' | '1440p' | '2160p';
export type ConcreteStreamQuality = Exclude<StreamQuality, 'auto'>;
export type StreamFrameRate = 'auto' | 24 | 30 | 60;

export const STREAM_QUALITY_ORDER: readonly ConcreteStreamQuality[] = [
  '720p',
  '1080p',
  '1440p',
  '2160p',
];

export interface RemoteStreamSettings {
  quality: StreamQuality;
  frameRate: StreamFrameRate;
}

export interface ScreenStreamProfile {
  label: string;
  width: number;
  height: number;
  maxFrameRate: number;
  maxBitrate: number;
}

export const DEFAULT_REMOTE_STREAM_SETTINGS: RemoteStreamSettings = {
  // 首次连接给出清晰、稳定的默认体验；用户仍可切回自动或省流量档位。
  quality: '1080p',
  frameRate: 30,
};

// 清晰度优先使用均衡档；需要和 Google Remote 等软件同时运行时可切换为省资源档。
export const DEFAULT_STREAM_PROFILE: StreamProfile = 'balanced';

export const STREAM_PROFILES: Record<StreamProfile, ScreenStreamProfile> = {
  'low-latency': {
    label: '省资源 / 低延迟',
    width: 1600,
    height: 900,
    maxFrameRate: 24,
    maxBitrate: 5_000_000,
  },
  balanced: {
    label: '均衡',
    width: 1920,
    height: 1080,
    maxFrameRate: 30,
    // 屏幕文字比普通摄像头画面更吃码率；提高上限可减少细字和细线的涂抹。
    maxBitrate: 12_000_000,
  },
  quality: {
    label: '清晰优先',
    width: 2560,
    height: 1440,
    maxFrameRate: 30,
    maxBitrate: 20_000_000,
  },
};

const QUALITY_PROFILES: Record<Exclude<StreamQuality, 'auto'>, ScreenStreamProfile> = {
  '720p': {
    label: '720p',
    width: 1280,
    height: 720,
    maxFrameRate: 30,
    maxBitrate: 5_000_000,
  },
  '1080p': {
    label: '1080p',
    width: 1920,
    height: 1080,
    maxFrameRate: 30,
    // 1080p 是默认清晰档；桌面文字比普通视频更需要码率。
    maxBitrate: 12_000_000,
  },
  '1440p': {
    label: '1440p',
    width: 2560,
    height: 1440,
    maxFrameRate: 30,
    maxBitrate: 20_000_000,
  },
  '2160p': {
    label: '2160p',
    width: 3840,
    height: 2160,
    maxFrameRate: 30,
    maxBitrate: 28_000_000,
  },
};

export function normalizeStreamProfile(value: unknown): StreamProfile {
  return value === 'balanced' || value === 'quality' || value === 'low-latency'
    ? value
    : DEFAULT_STREAM_PROFILE;
}

/** 自动画质的起始档位，保留用户在本机选择的资源策略。 */
export function streamQualityForProfile(profile: StreamProfile): ConcreteStreamQuality {
  switch (normalizeStreamProfile(profile)) {
    case 'low-latency': return '720p';
    case 'quality': return '1440p';
    default: return '1080p';
  }
}

/** 自动画质的上限，避免省资源模式在网络好时突然占满上行带宽。 */
export function streamQualityCeilingForProfile(profile: StreamProfile): ConcreteStreamQuality {
  switch (normalizeStreamProfile(profile)) {
    case 'low-latency': return '1080p';
    case 'quality': return '2160p';
    default: return '1440p';
  }
}

export function normalizeStreamQuality(value: unknown): StreamQuality {
  return value === '720p' || value === '1080p' || value === '1440p' || value === '2160p' || value === 'auto'
    ? value
    : 'auto';
}

export function normalizeStreamFrameRate(value: unknown): StreamFrameRate {
  return value === 24 || value === 30 || value === 60 || value === 'auto'
    ? value
    : 'auto';
}

export function normalizeRemoteStreamSettings(value: unknown): RemoteStreamSettings {
  const settings = (value || {}) as { quality?: unknown; frameRate?: unknown };
  return {
    quality: settings.quality == null ? DEFAULT_REMOTE_STREAM_SETTINGS.quality : normalizeStreamQuality(settings.quality),
    frameRate: settings.frameRate == null ? DEFAULT_REMOTE_STREAM_SETTINGS.frameRate : normalizeStreamFrameRate(settings.frameRate),
  };
}

/**
 * 把控制端本次会话的偏好解析为被控端采集/编码约束。
 * auto 保留被控端自己的默认档位，手动选择只覆盖对应维度。
 */
export function resolveStreamProfile(
  baseProfile: StreamProfile,
  remoteSettings: RemoteStreamSettings = DEFAULT_REMOTE_STREAM_SETTINGS,
): ScreenStreamProfile {
  const base = STREAM_PROFILES[normalizeStreamProfile(baseProfile)];
  const quality = normalizeStreamQuality(remoteSettings.quality);
  const frameRate = normalizeStreamFrameRate(remoteSettings.frameRate);
  const selected = quality === 'auto' ? base : QUALITY_PROFILES[quality];
  const selectedFrameRate = frameRate === 'auto' ? selected.maxFrameRate : frameRate;
  const frameRateScale = frameRate === 60 ? 1.35 : frameRate === 24 ? 0.88 : 1;

  return {
    ...selected,
    maxFrameRate: selectedFrameRate,
    maxBitrate: Math.round(selected.maxBitrate * frameRateScale),
  };
}
